import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Download, FileText } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeePayslips = () => {
  const { getAuthHeader } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayslips();
  }, []);

  const fetchPayslips = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employee/payslips`, {
        headers: getAuthHeader()
      });
      setPayslips(response.data);
    } catch (error) {
      console.error('Error fetching payslips:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (month) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/employee/payslips/${month}/download`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'  // Important for PDF download
        }
      );
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payslip_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading payslip:', error);
      alert('Failed to download payslip. Please try again.');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Payslips</h2>

      {payslips.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {payslips.map((payslip) => (
            <Card key={payslip.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="text-primary" size={24} />
                  {payslip.month}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Basic Salary:</span>
                    <span className="font-medium">₹{payslip.basic_salary.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">HRA:</span>
                    <span className="font-medium">₹{payslip.hra.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Allowances:</span>
                    <span className="font-medium">₹{payslip.other_allowances.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-2">
                    <span className="text-gray-600">Gross Salary:</span>
                    <span className="font-semibold">₹{payslip.gross_salary.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Deductions:</span>
                    <span>-₹{(payslip.pf + payslip.tax + payslip.other_deductions).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 text-lg">
                    <span className="font-bold">Net Salary:</span>
                    <span className="font-bold text-green-600">₹{payslip.net_salary.toLocaleString()}</span>
                  </div>
                </div>
                <Button
                  onClick={() => handleDownload(payslip.month)}
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  <Download size={16} className="mr-2" />
                  Download Payslip
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">No payslips available yet</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmployeePayslips;
